import { useEffect, useCallback, useRef } from 'react';

/**
 * useSwipeGestures - Detects swipe gestures on touch devices
 * 
 * @param {Object} options - Configuration options
 * @param {number} options.threshold - Minimum distance for swipe (default: 50px)
 * @param {number} options.restraint - Maximum perpendicular distance (default: 100px)
 * @param {number} options.allowedTime - Maximum time for swipe (default: 500ms)
 * @param {Function} options.onSwipeUp - Callback for swipe up
 * @param {Function} options.onSwipeDown - Callback for swipe down
 * @param {Function} options.onSwipeLeft - Callback for swipe left
 * @param {Function} options.onSwipeRight - Callback for swipe right
 * @param {boolean} options.enabled - Enable/disable gesture detection (default: true)
 * 
 * @returns {Object} - Ref to attach to element and gesture state
 */
export const useSwipeGestures = (options = {}) => {
  const {
    threshold = 50,
    restraint = 100,
    allowedTime = 500,
    onSwipeUp,
    onSwipeDown,
    onSwipeLeft,
    onSwipeRight,
    enabled = true,
  } = options;

  const elementRef = useRef(null);
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const touchStartTime = useRef(0);
  const isSwiping = useRef(false);

  const handleTouchStart = useCallback((e) => {
    if (!enabled) return;
    
    const touch = e.touches[0];
    touchStartX.current = touch.clientX;
    touchStartY.current = touch.clientY;
    touchStartTime.current = Date.now();
    isSwiping.current = false;
  }, [enabled]);

  const handleTouchMove = useCallback((e) => {
    if (!enabled || !touchStartX.current) return;
    
    const touch = e.touches[0];
    const distX = touch.clientX - touchStartX.current;
    const distY = touch.clientY - touchStartY.current;
    
    // Check if moving enough to be considered a swipe
    if (Math.abs(distX) > 10 || Math.abs(distY) > 10) {
      isSwiping.current = true;
    }
  }, [enabled]);

  const handleTouchEnd = useCallback((e) => {
    if (!enabled || !isSwiping.current) return;
    
    const touch = e.changedTouches[0];
    const distX = touch.clientX - touchStartX.current;
    const distY = touch.clientY - touchStartY.current;
    const elapsedTime = Date.now() - touchStartTime.current;

    // Check if swipe was fast enough
    if (elapsedTime > allowedTime) {
      return;
    }

    const absDistX = Math.abs(distX);
    const absDistY = Math.abs(distY);

    // Determine swipe direction
    if (absDistX >= threshold && absDistY <= restraint) {
      // Horizontal swipe
      if (distX > 0) {
        // Swipe right
        if (onSwipeRight) {
          e.preventDefault();
          onSwipeRight({ distX, distY, elapsedTime });
          
          // Haptic feedback
          if (navigator.vibrate) {
            navigator.vibrate(10);
          }
        }
      } else {
        // Swipe left
        if (onSwipeLeft) {
          e.preventDefault();
          onSwipeLeft({ distX, distY, elapsedTime });
          
          // Haptic feedback
          if (navigator.vibrate) {
            navigator.vibrate(10);
          }
        }
      }
    } else if (absDistY >= threshold && absDistX <= restraint) {
      // Vertical swipe
      if (distY > 0) {
        // Swipe down
        if (onSwipeDown) {
          e.preventDefault();
          onSwipeDown({ distX, distY, elapsedTime });
          
          // Haptic feedback
          if (navigator.vibrate) {
            navigator.vibrate(10);
          }
        }
      } else {
        // Swipe up
        if (onSwipeUp) {
          e.preventDefault();
          onSwipeUp({ distX, distY, elapsedTime });
          
          // Haptic feedback
          if (navigator.vibrate) {
            navigator.vibrate(10);
          }
        }
      }
    }

    // Reset
    touchStartX.current = 0;
    touchStartY.current = 0;
    touchStartTime.current = 0;
    isSwiping.current = false;
  }, [enabled, threshold, restraint, allowedTime, onSwipeUp, onSwipeDown, onSwipeLeft, onSwipeRight]);

  useEffect(() => {
    const element = elementRef.current;
    if (!element || !enabled) return;

    // Add touch event listeners
    element.addEventListener('touchstart', handleTouchStart, { passive: true });
    element.addEventListener('touchmove', handleTouchMove, { passive: true });
    element.addEventListener('touchend', handleTouchEnd, { passive: false });

    return () => {
      element.removeEventListener('touchstart', handleTouchStart);
      element.removeEventListener('touchmove', handleTouchMove);
      element.removeEventListener('touchend', handleTouchEnd);
    };
  }, [enabled, handleTouchStart, handleTouchMove, handleTouchEnd]);

  return {
    ref: elementRef,
    isSwiping: isSwiping.current,
  };
};

export default useSwipeGestures;
